import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Badge, Button, Card, CardBody, CardTitle, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import ReadingAnimation from "components/import/ReadingAnimation";

// Import da file CSV/Excel in due passi: anteprima (niente salvato) e conferma.
// api: { template(), run(formData) }; statuses: { codice: [etichetta, colore badge] }; counters: [[chiave, etichetta]]
const errText = (e) => e?.message || e?.data?.message || i18n.t("common:fileImport.failed");

const isPdfFile = (f) => !!f && (f.type === "application/pdf" || /\.pdf$/i.test(f.name || ""));
const money = (v) => `€ ${Number(v).toLocaleString(i18n.language === "en" ? "en-GB" : "it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Campi modificabili di un vino letto dal PDF (stesse chiavi del backend).
const WINE_FIELDS = [
  ["name", "fileImport.fields.name"], ["winery", "fileImport.fields.winery"], ["vintage", "fileImport.fields.vintage"], ["price", "fileImport.fields.price"],
  ["colour", "fileImport.fields.colour"], ["region", "fileImport.fields.region"], ["denomination", "fileImport.fields.denomination"], ["grapes", "fileImport.fields.grapes"],
];

// allowPdf: accetta anche la carta in PDF (letta dall'AI); l'elenco letto resta nell'anteprima e si rimanda alla conferma.
const FileImport = ({ title, intro, columnsHelp, api, templateName, statuses, counters, confirmLabel, doneHint, allowPdf }) => {
  const { t } = useTranslation("common");
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [editing, setEditing] = useState(null); // { index, values }

  const downloadTemplate = async () => {
    try {
      const text = await api.template();
      const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = templateName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };

  const send = async (dryRun) => {
    if (!file) return ErrorToaster(t("fileImport.chooseFile"));
    const fd = new FormData();
    if (!dryRun && preview?.source === "pdf") {
      // Conferma di un PDF: si importa l'elenco visto in anteprima, senza una seconda lettura dell'AI.
      fd.append("wines", JSON.stringify(preview.wines || []));
    } else {
      fd.append("file", file);
    }
    fd.append("dry_run", dryRun ? "true" : "false");
    setBusy(true);
    try {
      const r = await api.run(fd);
      if (dryRun) {
        setPreview(r.data);
        setDone(null);
      } else {
        setDone(r.data);
        setPreview(null);
        setFile(null);
        if (input.current) input.current.value = "";
        successToaster(r.message || t("fileImport.completed"));
      }
    } catch (e) {
      const message = e?.message || e?.data?.message;
      ErrorToaster(message || (isPdfFile(file) ? t("fileImport.pdfFailed") : t("fileImport.failed")));
    } finally {
      setBusy(false);
    }
  };

  // Elenco letto dal PDF corretto a mano (riga modificata o tolta): si ripete solo il controllo, senza una nuova lettura AI.
  const recheck = async (wines) => {
    if (!wines.length) {
      setPreview(null);
      setFile(null);
      if (input.current) input.current.value = "";
      return ErrorToaster(t("fileImport.removedAll"));
    }
    const fd = new FormData();
    fd.append("wines", JSON.stringify(wines));
    fd.append("dry_run", "true");
    setBusy(true);
    try {
      const r = await api.run(fd);
      setPreview({ ...r.data, warning: preview?.warning });
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (row) => {
    const index = row.row - 1;
    const wine = preview?.wines?.[index];
    if (!wine) return;
    const values = {};
    WINE_FIELDS.forEach(([k]) => { values[k] = wine[k] == null ? "" : String(wine[k]); });
    setEditing({ index, values });
  };

  const saveEdit = async () => {
    const clean = {};
    WINE_FIELDS.forEach(([k]) => { const v = (editing.values[k] || "").trim(); if (v) clean[k] = v; });
    if (!clean.name) return ErrorToaster(t("fileImport.nameRequired"));
    const wines = preview.wines.map((w, i) => (i === editing.index ? { ...w, ...clean } : w));
    WINE_FIELDS.forEach(([k]) => { if (!clean[k]) delete wines[editing.index][k]; });
    setEditing(null);
    await recheck(wines);
  };

  const removeRow = (row) => recheck(preview.wines.filter((_, i) => i !== row.row - 1));

  const pdf = isPdfFile(file);
  const showPrice = !!(preview || done)?.rows?.some((r) => r.price != null);
  const shown = preview || done;
  const actionable = preview ? counters.filter(([k]) => k !== "skipped" && k !== "errors").reduce((n, [k]) => n + (preview[k] || 0), 0) : 0;
  const editable = preview?.source === "pdf" && Array.isArray(preview?.wines);
  const rows = shown ? shown.rows.filter((r) => !onlyProblems || r.status === "error" || r.status === "skipped") : [];

  return (
    <div className="content">
      <Card>
        <CardBody>
          <CardTitle tag="h4">{title}</CardTitle>
          <p className="text-muted">{intro}</p>
          <ol className="pl-3">
            <li className="mb-2">
              {t("fileImport.step1")}{" "}
              <Button size="sm" onClick={downloadTemplate}>{t("fileImport.downloadTemplate")}</Button>
              {columnsHelp && <div className="small text-muted mt-1">{columnsHelp}</div>}
            </li>
            <li className="mb-2">
              {t("fileImport.step2")}{allowPdf ? t("fileImport.step2Pdf") : ""}:
              <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: 8 }}>
                <input ref={input} type="file" accept={`.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet${allowPdf ? ",.pdf,application/pdf" : ""}`}
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setDone(null); }} />
                <Button color="primary" className="m-0" onClick={() => send(true)} disabled={!file || busy}>
                  {busy && !preview ? (pdf ? t("fileImport.readingPdf") : t("fileImport.checking")) : pdf ? t("fileImport.readPdf") : t("fileImport.checkFile")}
                </Button>
              </div>
              {busy && !preview && <ReadingAnimation pdf={pdf} />}
            </li>
            <li>{t("fileImport.step3")}</li>
          </ol>
        </CardBody>
      </Card>

      {shown && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
              <div>
                <CardTitle tag="h4" className="mb-1">{preview ? t("fileImport.preview") : t("fileImport.completed")}</CardTitle>
                <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                  {counters.map(([k, l]) => (
                    <span key={k}><b style={{ fontSize: 20 }}>{shown[k] || 0}</b> <span className="text-muted">{l}</span></span>
                  ))}
                </div>
                {shown.ignored_columns?.length > 0 && (
                  <div className="small text-muted mt-1">{t("fileImport.ignoredColumns")}: {shown.ignored_columns.join(", ")}</div>
                )}
                {preview?.source === "pdf" && (
                  <div className="small text-info mt-2">{t("fileImport.aiNotice")}</div>
                )}
                {preview?.warning && <div className="small text-warning mt-1">⚠ {preview.warning}</div>}
                {done && doneHint && <div className="small mt-2">{doneHint}</div>}
              </div>
              {preview && (
                <Button color="primary" onClick={() => send(false)} disabled={busy || actionable === 0}>
                  {busy ? t("fileImport.importing") : actionable === 0 ? t("fileImport.nothingToImport") : `${confirmLabel} (${actionable})`}
                </Button>
              )}
            </div>
            <label className="small mt-3 mb-2" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> {t("fileImport.onlyProblems")}
            </label>
            <Table size="sm" responsive className="mb-0">
              <thead><tr><th style={{ width: 70 }}>{t("fileImport.colRow")}</th><th>{t("fileImport.colWine")}</th>{showPrice && <th className="text-right">{t("fileImport.colPrice")}</th>}<th>{t("fileImport.colOutcome")}</th><th>{t("fileImport.colDetail")}</th>{editable && <th className="text-right" style={{ width: 96 }}>{t("fileImport.colActions")}</th>}</tr></thead>
              <tbody>
                {rows.map((r) => {
                  const [label, color] = statuses[r.status] || [r.status, "secondary"];
                  return (
                    <tr key={r.row}>
                      <td>{r.row}</td>
                      <td>
                        {r.wine || r.match || <span className="text-muted">—</span>}
                        {r.match && r.wine && r.match.toLowerCase() !== r.wine.toLowerCase() && <div className="small text-muted">{t("fileImport.found")}: {r.match}</div>}
                      </td>
                      {showPrice && <td className="text-right">{r.price != null ? money(r.price) : <span className="text-muted">—</span>}</td>}
                      <td><Badge color={color}>{label}</Badge></td>
                      <td className="small">{r.message}</td>
                      {editable && (
                        <td className="text-right text-nowrap">
                          <Button size="sm" color={r.status === "error" ? "warning" : "info"} className="btn-icon btn-simple m-0 mr-1" title={t("fileImport.editTitle")} aria-label={t("fileImport.edit")} disabled={busy} onClick={() => openEdit(r)}>
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <Button size="sm" color="danger" className="btn-icon btn-simple m-0" title={t("fileImport.removeTitle")} aria-label={t("fileImport.remove")} disabled={busy} onClick={() => removeRow(r)}>
                            <i className="tim-icons icon-trash-simple" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={!!editing} toggle={() => setEditing(null)}>
        <ModalHeader toggle={() => setEditing(null)}>{t("fileImport.editWine")}</ModalHeader>
        <ModalBody>
          {editing && WINE_FIELDS.map(([k, labelKey]) => (
            <div className="mb-2" key={k}>
              <label className="small mb-1">{t(labelKey)}</label>
              <Input
                value={editing.values[k]}
                onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [k]: e.target.value } })}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }}
              />
            </div>
          ))}
          <div className="small text-muted">{t("fileImport.recheckHint")}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditing(null)}>{t("fileImport.cancel")}</Button>
          <Button color="primary" onClick={saveEdit}>{t("fileImport.saveRecheck")}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FileImport;
